import React, { useState, useref } from "react";
import "./IslamicTrade.css";
import ProgressTracker from "../components/ProgressTracker";
import QuoteCard from "../components/QuoteCard";
import FloatingPageNav from "../components/FloatingPageNav";
import BadgePopup from "../components/Badgepopup";
import { Link } from "react-router-dom";

function DIY96() {
  // State to handle answer visibility
  const [showAnswer, setShowAnswer] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  const links = {
    prev: "/foreword-diy",
    index: "/index-diy",
    next: "/diy-97",
  };

  // Function to handle "Check Answer" click
  const handleCheckAnswer = (e) => {
    e.preventDefault(); // Prevents the page from scrolling to the top
    setShowAnswer(true); // Show the correct answer when clicked
  };

  return (
    <div className="container">
     <ProgressTracker currentDay={3} totalDays={100} />
      <h1>DIY</h1>
        
      <h2>Headline 2</h2>
      <p className="content">
     
        Welcome to <b>Day 96</b>!
Today’s lesson marks an important milestone in your Bitcoin learning journey, as we shift from theory to real-world application by walking through the complete process of setting up a Bitcoin miner at home. Mining is more than just hashing for rewards; it is a hands-on way to contribute to Bitcoin’s decentralization and ensure that block creation remains distributed, censorship-resistant, and free from the control of a few large industrial operators. Whether you are setting up a machine in your garage, spare room, or dedicated workspace, this lesson will guide you step by step, from understanding why home mining matters, to assembling your equipment, connecting your device to the network, and finally configuring it to join a mining pool. By the end of today, you will not only know how to run a miner but also understand the deeper purpose behind doing so.<br/><br/>
      Bitcoin mining is more than a technical process. It is the very heartbeat of the Bitcoin network, a decentralized, global, permissionless system that relies on individuals rather than institutions to maintain its integrity. While many people understand the importance of running a node, far fewer take the time to consider how vital miners are in ensuring that block creation remains truly decentralized. Running a node allows every person to independently verify the rules, but mining determines how transactions are ordered and which ones are included in each block. This part of the system often receives less attention, even though it shapes the final structure of the blockchain.
<br/>Most Bitcoiners have heard the phrase “Don’t trust. Verify.” Running a node is what enables this philosophy. A node allows you to confirm that the Bitcoin you receive is valid and that the blocks you see follow consensus rules. But there is another layer to Bitcoin’s decentralization that is often overlooked. It involves the creation of block templates and the selection of transactions. Nodes verify blocks after they are broadcast, but miners build those blocks before they are seen by the network. The diversity of who builds those blocks is a major safeguard against influence or censorship. If block creation becomes concentrated in the hands of a few large operations, then the possibility of manipulation becomes more realistic even if those operations behave honestly most of the time.
<br/>Mining is the execution engine of Bitcoin’s consensus rules. Without broad and distributed mining power, the network is more vulnerable to subtle forms of pressure that could influence which transactions are included, how they are prioritized, or how the mempool is processed. Even if every node in the world remains independent, a concentration of hash power still creates potential risk in the stage where blocks are constructed. This is why decentralizing miners is just as important as decentralizing nodes.
<br/>It is true that mining requires significantly more infrastructure and energy than running a node. A node can operate on a small device with modest requirements. Mining, on the other hand, demands specialized ASIC hardware, stable electrical capacity, cooling, space, and an understanding of heat and noise management. Because of these differences, many people assume that mining is only feasible for large industrial facilities. They imagine huge warehouses in remote areas, filled with thousands of machines operating around the clock.
<br/>This assumption creates the impression that home mining is neither practical nor useful. The reality is very different. Mining at home, even if it is done on a small scale, contributes meaningfully to the decentralization of hash rate. Every individual who runs a miner at home adds resilience to the system. A single ASIC in a garage, basement, or spare room matters in ways that go beyond the number of hashes it produces. It is a unique point of independence that cannot easily be influenced by corporations, governments, or external pressures.
<br/>Home mining is not only about earning sats. It is a form of sovereignty and participation. It is a way to support the network not just by verifying but also by helping secure it at the level where blocks are created. Many miners who operate at home begin their journey not because they expect large financial returns but because they want to contribute to Bitcoin in a direct and meaningful way. They want to play a role in protecting the network from centralization of block production.
<br/>If no one mined Bitcoin at home, mining would be left entirely to giant industrial operations in places with access to inexpensive electricity. Bitcoin would still function, but it would be significantly more fragile. Consolidation of mining creates potential attack surfaces. Governments could pressure commercial miners. Financial institutions could propose transaction filtering. Energy providers might prefer to work with a few large customers instead of thousands of small ones. All of these factors make the entire network easier to influence. Distributed mining is one of Bitcoin’s strongest defenses against these risks.
<br/>Your home miner is one of many thousands of independent machines spread across the world. Each one represents a point of resistance to centralization. When you run a miner at home, you are strengthening the network even if your machine contributes only a small fraction of the total hash rate. Mining is not only about quantity. It is also about distribution, jurisdictional diversity, and independent operation.
<br/>This brings us to the purpose of this lesson. Many people want to try home mining but feel overwhelmed by the technical details. They may not know how to choose hardware, how to set up electrical connections safely, how to manage heat, or how to configure firmware. They may think that mining is complicated, expensive, or impractical. But once the steps are broken down clearly, the process becomes more accessible than most people expect.
<br/>This DIY lesson is designed to make mining approachable for anyone who has curiosity, basic technical comfort, and a desire to support Bitcoin. The goal is not only to teach you how to set up a miner but also to explain why each step matters and what role it plays in the broader network. When you finish the lesson, you will understand both the philosophy and the practical mechanics behind home mining.
<br/>Before we get into the details of wiring, mounting, ventilation, firmware settings, or mining pool configuration, we need to establish the foundation. This first section is all about the role of miners in the ecosystem and why home mining provides real value to Bitcoin despite the dominance of large scale operations.
<br/><br/>
     <b>Mining as the Backbone of Bitcoin</b><br/><br/>
A Bitcoin miner serves two fundamental purposes:<br/><br/>
1. Securing the network by providing proof-of-work.<br/>
Every hash your machine produces represents compute energy expended to protect the network from double-spending and other forms of attack.
<br/>2. Constructing the block template.<br/>
This is where transaction ordering happens. Miners choose which transactions to include, based on fees and mempool state. Decentralizing this activity ensures no entity can prioritize, censor, or reorder transactions for political or financial agendas.
<br/>Most people talk about the first function. Very few discuss the second. But block template construction is where real decentralization is tested.
<br/>When a block is mined, it becomes part of Bitcoin’s permanent ledger. With enough decentralization across miners, no single entity controls the composition of blocks. Without this decentralization, even a well-distributed network of nodes cannot fully protect against subtle forms of influence over which transactions get included and when.
<br/>This is why home miners matter.
The Reality of Mining Infrastructure<br/><br/>
It is true that setting up a miner at home demands more effort than simply running a node. But the gap is not as big as people imagine.
<br/>
</p>
<table className="trade-table">
    <thead>
        <tr>
            <th>Requirements to set-up a Bitcoin Miner</th>
        </tr>
       
            <td>
An ASIC (like an Antminer or Whatsminer)
            </td>
            <tr/>
            <tr>
            <td>
Stable electricity with adequate amperage
            </td>
            </tr>
              <tr>
            <td>
Ventilation or a cooling plan
            </td>
            </tr>
              <tr>
            <td>
Stable internet connection
            </td>
            </tr>
                <tr>
            <td>
Occasional Monitoring
            </td>
            </tr>
    
    </thead>
</table>
<p className="content">








In practice, many homes already possess the electrical capacity required to run at least one machine safely. Garages, utility rooms, or spare rooms can often support a miner with minor adjustments. Even without industrial-grade infrastructure, a single ASIC is manageable, and with proper cooling or firmware tuning, noise and heat can be controlled.
<br/>Some people even run low-powered or silent miners (e.g., Bitaxe, Nerdminer, or repurposed S9s with custom firmware), which offer a quieter, more energy-efficient way to participate in mining even if the hash rate is smal, which we will cover in the subsequent lesson. These devices don’t contribute much to earnings, but they make mining accessible for experimentation, learning, and decentralization.
<br/>Home mining is no longer an obscure hobby. It is a growing movement, one that mirrors the early days of running Bitcoin full nodes. As more people realize that hashing isn’t only about profit, but about contributing to the network’s security and censorship resistance, small-scale mining becomes an essential part of the broader mission of Bitcoin.
<br/><br/><b>Why You Should Consider Mining at Home</b><br/><br/>

People who mine at home usually fall into one of several categories:
<ul>
    <li>The decentralization advocate:
They mine to keep hash rate distributed and block creation independent from industrial players.</li>
<li>The bitcoiner seeking sovereignty:
They want to contribute to Bitcoin’s foundation and be part of the creation process of the very asset they hold.</li>
<li>
The technologist or tinkerer:
They enjoy setting up hardware, optimizing firmware, and experimenting with energy and cooling.
</li>
<li>
The opportunistic miner:
They have access to cheap or surplus electricity (e.g., solar excess, rural tariffs, off-peak rates) and want to convert that unused energy into Bitcoin.
</li>
<li>
Those who want heat + sats:
Because ASICs output heat, they can be used to offset heating costs while simultaneously generating Bitcoin.
</li>
</ul>

<br/>No matter your reason, you are strengthening Bitcoin simply by participating. Even one miner running in a garage is part of a global lattice of independent hashing nodes that collectively protect the network from attack and maintain Bitcoin’s neutrality. 
      
      <br/><br/><b>A Step Toward a More Decentralized Bitcoin</b><br/><br/>
Home mining will never replace industrial mining entirely, and it doesn’t need to. Bitcoin is designed to allow both. Large miners provide significant hash power, while home miners provide resilience, diversity, and decentralization. Together they form a balanced ecosystem.
<br/>Think of each home miner as a small but critical node in a much larger mesh. If one industrial miner fails or chooses to censor, home miners continue hashing. If a region experiences an outage or regulatory pressure, miners in other parts of the world still contribute. The more distributed miners are geographically, electrically, and economically, the safer Bitcoin becomes.
<br/>You are not just setting up a machine. You are participating in the global effort to ensure that Bitcoin remains neutral, permissionless, and resistant to coercion. You’re helping maintain the very characteristics that make Bitcoin unique.
      <br/><br/>
<b>Hardware Setup, Connection, and Preparing Your Home Miner for First Use</b>

<br/><br/>
</p>
<img src="DIY97_1.jpeg" width="350px"></img>
<p className="content"><br/>
In this section, we will walk through the practical steps needed to set up a Bitcoin miner at home. We will use my personal miner, the S J 19 90 TH/S model, as the reference device throughout this guide. This miner is one of the simplest and most affordable Bitcoin ASIC miners available today. Most mining equipment suppliers around the world sell it for a price between 1000 and 1200 dollars, and because the hardware is straightforward, it is an excellent starting point for anyone new to home mining.
<br/>
The goal of this section is to remove every layer of confusion about hardware preparation. Many first time miners assume that the setup process will be complex or overwhelming, but in reality most modern ASIC miners arrive fully assembled and ready to run. Your task is mainly to prepare the right cables, ensure your electrical supply is appropriate, connect the device to the internet, and complete a simple sequence of steps that allows your miner to join the network. Once this process is complete, you will be ready to move on to software configuration in Section 3.
<br/>
Let us begin by breaking everything down clearly and patiently so that no step feels rushed or unclear.

<br/><br/><b>Understanding the Miner and Its Components</b><br/><br/>
</p>
<img src="DIY97_2.jpeg" width="450px"></img><br/>
<p className="content">
The SJ 19 is a compact and practical Bitcoin miner that operates at around 90 terahashes per second. It arrives from the vendor in a sealed box, already assembled and tested. You do not need to put together any of the internal components. The hashing boards, control board, fans, power supply unit, wiring harness, and casing all come preinstalled. All you need to do is unbox the device, place it in the location where it will operate, and connect two types of cables.
<br/>
Although the miner arrives already assembled, many vendors provide diagrams or photographs of the miner with its internal parts highlighted. These images help users understand what is inside the device, what components are responsible for power distribution, which parts are responsible for cooling, and where the control board is located. Even though viewing these images is not necessary for operation, they help build familiarity with the machine. Since you are operating a device that draws significant power and generates high heat, it is useful to know where key components sit within the structure.
<br/>
The most important part of the miner from a user perspective is the back panel. This is where all connections happen. You will find two ports for power cables, one ethernet port, the IP report button, indicator lights, and sometimes a reset button. For the purpose of this guide, keep your attention mainly on the two power ports, the ethernet port, and the IP report button.

<br/><br/><b>Preparing Power Cables</b><br/><br/>

To power the SJ 19 miner, you need two separate power cables. Each cable must support 16 amperes because the miner draws a significant amount of electrical energy during operation. The cables do not come with the machine for a straightforward reason. Different regions around the world use different outlet standards, plug types, voltage standards, and connector shapes. Vendors cannot predict which country the device will be shipped to, so they leave the responsibility of cable selection to the customer.
<br/>
This is not a complicated task, but it is important to get right. When shopping for the proper cables, tell the vendor the following details.
<br/>
First, you need two 16 ampere power cables with the appropriate connector type for your local wall outlet standard. The ends that plug into the miner are uniform across countries, but the ends that plug into your wall socket will vary depending on where you live.
<br/>
Second, the cables should be thick enough to safely handle continuous high wattage loads. Most 16 ampere cables already meet this requirement, but it is good practice to check for a well insulated design, solid copper wiring, and heat resistant outer material. These are not exotic features. They are basic qualities of any cable designed for high current appliances such as heaters, air conditioners, or heavy computer hardware.
<br/>
Third, make sure the cables you buy are not too short. You want some flexibility in how you arrange your miner, especially if you need to position it in a location with good ventilation. A length of around one and a half meters is normally ideal for home environments.
<br/>
Once you have the right cables, examine your electrical outlets or power strips. The miner should always be plugged directly into a wall outlet rather than into a lightweight extension cord. If you absolutely must use an extension, it must be a heavy duty extension that supports high current appliances. Never use a low grade extension cord with a miner. The risk of overheating and electrical failure becomes unnecessarily high.
<br/>
If your home or room has voltage stability issues, you may also want to invest in a voltage stabilizer or a surge protector. Many miners run safely without these, but in some regions the power grid is less consistent and a protective device can help prevent damage to your ASIC or reduce operational interruptions.

<br/><br/><b>Preparing the Network Cable</b><br/><br/>
</p>
<img src="DIY97_3.jpeg" width="450px"></img>
<p className="content">
The miner requires a stable and reliable internet connection. While modern technology has made WiFi widely accessible, ASIC miners do not use WiFi. They connect to the internet through a wired ethernet connection. The reason for this is stability. A miner does not need high bandwidth, but it needs a consistent and uninterrupted connection. Even small interruptions in connectivity can cause your miner to lose work, disconnect from your mining pool, or operate at a reduced efficiency.
<br/>
Therefore you need a standard ethernet cable, usually labeled as Cat 5, Cat 5e, or Cat 6. Any of these will work for mining. The cable must run from the ethernet port on your miner directly into your WiFi router or into any ethernet outlet that provides a stable internet feed.
<br/>
Most homes have their routers installed in central locations such as hallways, living rooms, or study rooms. If your miner is placed far from your router, you may need a longer ethernet cable. These cables come in multiple lengths, and it is worth taking a moment to measure or estimate the distance so that you do not end up with a cable that is too short. Having extra length is generally better than stretching a cable tightly.
<br/>
Do not worry about advanced network settings at this stage. The miner only needs a physical wired connection. Once everything is plugged in, the miner will automatically negotiate its connection with your router during the IP reporting step described later.
<br/><br/><b>Positioning Your Miner in the Right Spot</b><br/><br/>


Before you plug the cables into the miner, it is important to choose an appropriate location for the device. ASIC miners generate a considerable amount of heat. They also produce significant noise because of the high speed fans that maintain cool internal temperatures.
<br/>
Consider the following guidelines:
<ul>
    <li>Choose a location with good airflow. Avoid small enclosed spaces such as closed cabinets or tightly packed storage areas. A miner needs a constant flow of air to avoid overheating. A garage, basement, or open utility room works well.</li>
    <li>Allow space behind the miner. The back of the device is where hot air is expelled. Always leave some distance between the miner and the wall to allow heat to dissipate. If the miner is too close to the wall, hot air will cycle back into the intake fans, causing internal temperatures to rise unnecessarily.</li>
    <li>Avoid moisture and dust. ASIC miners are sensitive to moisture. Do not place them near water sources, washing machines, or damp areas. Dust can also accumulate inside the fans and reduce airflow. If you place the miner in a dusty environment, clean the area regularly or use dust filters.</li>
    <li>Ensure proper noise tolerance. Miners are loud. If you place one in a living room, bedroom, or workspace, it may disturb your environment. Many miners therefore choose garages, balconies, unused rooms, or storage corners that are away from daily activity.</li>
</ul>

After finding the right place, make sure the cables can reach comfortably. Do not stretch power cables or ethernet cables too tightly.

<br/><br/><b>Connecting the Power Cables</b><br/><br/>

Once both power cables are ready, you will notice that your SJ 19 miner has two power ports located on the back side. Each port accepts one cable. The miner requires two separate cables because the internal power distribution system splits the electrical load between two circuits.
<br/>
Connect each power cable firmly. The connectors should slide in smoothly and sit securely in place. Check that the connection feels stable and that the cable is not loose.
<br/>
After connecting the cables to the miner, plug the other ends into the wall outlet. Make sure both cables are plugged in before turning on the miner. Some users mistakenly try to run the miner with only one cable connected. This can cause the machine to reboot repeatedly or fail to start properly.
<br/>
If your electrical outlet supports individual switches, turn the switches on only after both cables have been plugged in. If the outlet does not have switches, the miner will power on immediately when the cables make contact with the power source.

<br/><br/><b>Connecting the Ethernet Cable</b><br/><br/>

Next, take your ethernet cable and insert one end into the ethernet port on the miner. The port is clearly labeled and shaped in a way that prevents incorrect insertion.
<br/>
The other end of the cable should go into your router. If your router has multiple ethernet ports, you can plug it into any one of them. Most routers have indicator lights near the ethernet ports. When the miner is recognized by the router, the corresponding indicator light will usually blink or turn solid to confirm network activity.
<br/>
You do not need to adjust router settings at this stage. Modern routers automatically assign IP addresses to connected devices.
<br/><br/>
<b>Using the IP Report Button</b><br/><br/>


Once the miner is connected to power and ethernet, it is time to identify its IP address. This step allows you to access the miner’s user interface on your computer during the configuration stage.
<br/>
On the back of the miner, you will find a small button labeled IP Report. Press and hold this button for around five seconds. This triggers the miner to send a broadcast message to your local network. The message allows your router to assign an IP address to the miner and allows your computer to detect it when using IP scanning tools.
<br/>
Some miners also include a companion software tool that listens for these broadcast signals and automatically displays the miner’s IP address on your computer screen. If your device includes such a tool, it will be mentioned in the vendor’s documentation. If it does not, you can still find the IP address by using simple network scanning software or by checking your router’s list of connected devices.
<br/>
If the IP Report button does not seem to do anything on the first try, press it again for a slightly longer duration. Sometimes the miner needs a moment after first powering on before the network broadcast becomes active.
<br/>
Once you have identified the IP address, make a note of it. You will need it in Section 3 when we configure mining pools, firmware settings, and operational parameters.

<br/><br/><b>Confirming Successful Initialization</b><br/><br/>

After completing the steps above, the miner should be fully powered, connected to your network, and ready for software configuration. To confirm that everything is functioning as intended, look for the following signs.
<br/>
The fans on the miner should begin spinning. This indicates that the power supply is active and delivering current to the machine.
<br/>
The indicator lights near the ethernet port should flash or glow, confirming that a network connection has been established.
<br/>
You should hear the miner move through its startup cycle. The fans often spin at a high speed initially, then settle down as the control board completes its diagnostics.
<br/>
Within a minute or two, the miner should be reachable on your network at the IP address you recorded earlier.
<br/>
At this stage, the hardware portion of your setup is complete. You have successfully connected your miner to electricity, airflow, and the internet. In the next section, you will access the miner through your computer and configure it for actual mining.
<br/><br/>



<b>Connecting Your Miner to Your Network and Mining Pool</b> <br/><br/>

Once your Bitcoin miner has been physically set up, powered correctly, and connected to your local network using an Ethernet cable, the next phase of the process is configuring it through your local network interface. This is where you will link the miner to your router, assign it a stable IP address, and finally connect it to a mining pool through the stratum server. Even though these steps may initially appear technical, each task is methodical and can be followed without prior networking experience. In this section, we will walk through these steps in great detail, providing both explanations and practical guidance so that even a newcomer feels entirely comfortable navigating the miner’s configuration interface.

<br/><br/><b>Finding Your Router’s IP Address</b><br/><br/>

Before you can access the miner, you must first access your router’s configuration panel. Most household users rarely log into their router, so the default IP address is often unknown to them. Fortunately, almost all consumer-grade routers clearly display their default login details on a sticker located either at the bottom or back of the device. This sticker usually includes:<br/>
<ul>
  <li> The router model number</li>
  <li>Default Wi-Fi SSID and password</li>
  <li>Default administrator username and password</li>
  <li>The IP address used to access router settings</li>
</ul>


This IP address is almost always a private network address in one of the following ranges:<br/>
<ul>
  <li>192.168.0.1</li>
   <li>192.168.1.1</li>
    <li>192.168.100.1</li>
     <li>10.0.0.1</li>
</ul>


Once you locate this IP address (printed somewhere near “Login,” “Web Portal,” or “Admin Access”), open any web browser on a computer or smartphone that is connected to the same Wi-Fi network. Enter the IP address directly into the browser’s address bar. Do not type “www” or “http”, simply write the IP, for example:<br/>
192.168.1.1<br/>

Press Enter, and your browser will attempt to load the router’s login page. This is typically a simple interface that asks for a username and password. Many service providers configure the default credentials as:<br/>

<ul>
  <li>Username: admin</li>
    <li>Password: admin</li>
</ul>
  or
  <ul>
  <li>Username: admin</li>
    <li>Password: password</li>
</ul>


If you have changed these credentials previously, enter the updated login information. If you have never accessed the router before and the provided credentials do not work, you may need to check your service provider’s documentation or search the router model number online to find the correct default login details.<br/>


Once logged in successfully, you will be inside your router’s configuration panel. This is the control center for everything connected to your home network.<br/>


<br/><b>Locating the DHCP Client List</b><br/><br/>

Inside the router interface, you will find a navigation menu typically positioned on the left-hand side of the screen. Depending on your router manufacturer, whether TP-Link, D-Link, Asus, Huawei, JioFiber, Airtel, or another brand, the menu may look slightly different. However, all routers follow a similar network structure and therefore contain similar categories.<br/>
<br/>
</p>
<img src="DIY97_5.jpeg" width="450px"></img>
<p className="content">

Look for a menu item labeled “DHCP”, “LAN Settings,” or “Local Network.” DHCP stands for Dynamic Host Configuration Protocol, which is the system responsible for automatically assigning IP addresses to every device connected to your network. Your Bitcoin miner, when plugged in for the first time, receives its IP address through this DHCP system.<br/>


Click on DHCP, and then look for “DHCP Client List,” “Client Table,” or “Connected Devices.” This page contains a list of all devices currently connected to your network, each with the following information:<br/>
<ul>
  <li>Device name</li>
    <li>MAC address (hardware ID)</li>
      <li>Assigned IP address</li>
        <li>Connection type (Wi-Fi or LAN/Ethernet)</li>
</ul>



Your miner should be visible in this list because it is connected to your router through the Ethernet cable. Depending on your miner’s model, it may appear under a name like:<br/>
<ul>
  <li>Antminer</li>
  <li>ASICMiner</li>
  <li>Unknown device</li>
  <li>A long alphanumeric string corresponding to its MAC address</li>
</ul>

</p>
<img src="DIY97_6.jpeg" width="750px"></img>
<p className="content">

If the name is not clearly labeled, look for the device that appears connected via LAN rather than Wi-Fi. Miners do not use Wi-Fi, so the device associated with a wired connection is most likely your ASIC.<br/>

Once you identify your miner, copy the assigned IP address associated with it. This is the address you will use to access the miner’s internal dashboard.<br/>


<br/><b>Accessing the Miner Through Its IP Address</b><br/><br/>

Open a new browser tab and paste the miner’s IP address into the address bar exactly as it appears. It may look like something similar to:<br/>

192.168.1.23
<br/>
Press Enter. This should load the miner’s login page. If this is the first time you are accessing the device, it will prompt you for a username and password. Most Bitcoin ASIC miners, including the models from Bitmain (Antminer series), MicroBT (Whatsminer series), and Goldshell, follow the same default credentials.<br/>
<br/>

Enter the username:<br/>

root
<br/>
Enter the password:
<br/>
root
<br/>
</p>
<img src="DIY97_4.jpeg" width="450px"></img>
  <p className="content">
This simple login format is used because miners are generally expected to operate within a secure local network rather than exposed publicly. Once logged in, it is good practice to change the default password later to prevent unauthorized access, especially if others share your local network.<br/>

After entering the credentials, you will be taken to the miner’s dashboard. For the first time, you will now be able to see all technical and operational details of your mining machine.<br/>

<br/><b>Understanding the Miner Dashboard</b><br/><br/>

Your miner’s control panel usually contains several tabs such as:<br/>
<ul>
  <li>Dashboard / Home</li>
    <li>Miner Configuration</li>
      <li>Miner Status</li>
        <li>Network Settings</li>
        <li>System</li>
</ul>

<br/>

The home dashboard typically displays real-time statistics including:<br/>
<ul>
  <li>Hashrate</li>
   <li>Fan speeds</li>
    <li>Temperature of each hashboard</li>
     <li>Network connection status</li>
      <li>Uptime duration</li>
      <li>Accepted and rejected shares</li>
</ul>

For now, the most important section is Miner Configuration, because this is where you will connect the miner to your chosen mining pool.<br/><br/>

<b>Entering Your Mining Pool Information</b><br/><br/>

Every miner must be connected to a mining pool unless you are solo mining. Pool mining is the most popular approach.<br/>

Inside Miner Configuration, you will see fields known as:
<ul>
  <li>Pool 1</li>
   <li>Pool 2</li>
    <li>Pool 3</li>
</ul>
</p>
<img src="DIY97_8.jpeg" width="750px"></img>
<p className="content">

Your miner will attempt to connect to Pool 1 first. Pool 2 and Pool 3 are backup pools in case Pool 1 becomes unreachable.<br/>

Each pool requires two pieces of information:
<ul>
  <li>Stratum server address</li>
   <li>Worker name (username)</li>
</ul>


Most mining pools display this information in their “Getting Started” or “Connect Miner” section. <br/>
For example, a typical pool URL looks like:<br/>


stratum+tcp://pool.ocean.xyz:3333

<br/>
And your worker name is usually formatted like:

<br/>
walletaddress.workername<br/>

or simply:

<br/>
walletaddress
<br/>
Paste the pool address into the URL field for Pool 1.<br/>

Paste your worker name into the Worker or Username field.<br/>

Leave the password field either blank or set to “x” if required; this field is not used for authentication in most mining pools.<br/>

Repeat these steps for Pool 2 and Pool 3 if you want backups. These can be other stratum addresses from the same pool or different pools entirely.<br/>
<br/><b>Saving and Applying the Settings</b><br/><br/>
</p>
<img src="DIY97_7.jpeg" width="450px"></img>
<p className="content">
Once the pool information has been entered, scroll down and locate the Save & Apply button. When you click this, the miner will reboot its mining process and attempt to establish a connection with the pool. This may take anywhere from 10 seconds to 2 minutes.<br/>

If the connection is successful, the dashboard will begin displaying:
<ul>
  <li>Shares being submitted</li>
   <li>Your live hash rate</li>
    <li>The miner’s chip temperatures</li>
     <li>Accepted jobs from the pool</li>
</ul>


If you see data increasing steadily, you are officially mining Bitcoin!!<br/>

<br/><b>Verifying Connection From the Pool Side</b><br/><br/>

Most pools provide a user dashboard where you can monitor your miner’s performance. Log into your mining pool account and navigate to the statistics page. Within a few minutes, your miner should appear as “Online,” “Active,” or “Connected.”<br/>

You should also see:
<ul>
  <li>Hashrate readings</li>
   <li>Worker name</li>
    <li>Submitted shares</li>
     <li>Estimated earnings</li>
</ul>


It is normal for the displayed hashrate to fluctuate significantly during the first hour as the pool gathers enough data to calculate average performance.<br/>

<br/>
      
      <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        This lesson walks you through the complete process of setting up a Bitcoin miner , starting with the importance of decentralized hashrate and why small, independent cypherpunk miners play a crucial role in strengthening Bitcoin’s security and block template diversity. You then learn about the physical setup of a basic ASIC miner, including required cables, power considerations, network connectivity, and how to identify the device on your local network. Finally, the lesson guides you through accessing the miner’s dashboard, configuring pool settings through the stratum server, entering your wallet or worker information, and verifying that the machine is successfully submitting shares. By the end, you understand not only how to get a miner up and running but also why participating in mining, no matter how small your hashrate, contributes to Bitcoin’s resilience and long-term decentralization.<br/><br/>
         <b> Coming Up Next</b>
        <br />
        <br />
      </p>
      <h2>Test your understanding</h2>
      <p className="content">
        Which of the following falls under the definition of a valid and
        permissible Trade(<em>Bayʿ</em>) in Islam?
        <ul>
          <li>A. </li>
          <li>B. </li>
          <li>C. </li>
          <li>D. </li>
        </ul>
      </p>
        <p className="content">
        Which of the following falls under the definition of a valid and
        permissible Trade(<em>Bayʿ</em>) in Islam?
        <ul>
          <li>A. </li>
          <li>B. </li>
          <li>C. </li>
          <li>D. </li>
        </ul>
      </p>
        <p className="content">
        Which of the following falls under the definition of a valid and
        permissible Trade(<em>Bayʿ</em>) in Islam?
        <ul>
          <li>A. </li>
          <li>B. </li>
          <li>C. </li>
          <li>D. </li>
        </ul>
      </p>
        <p className="content">
        Which of the following falls under the definition of a valid and
        permissible Trade(<em>Bayʿ</em>) in Islam?
        <ul>
          <li>A. </li>
          <li>B. </li>
          <li>C. </li>
          <li>D. </li>
        </ul>
      </p>
      {/* Link that triggers the reveal of the answer */}
      <a className="content" href="#" onClick={handleCheckAnswer}>
        Check Answers
      </a>

      {/* Display answer if showAnswer state is true */}
      {showAnswer && (
        <p className="content" id="answer">
          The correct answers are:
        </p>
      )}

      <div className="button-container">
        <Link to="/economics">
          <button className="page-button">Go Back</button>
        </Link>
        <Link to="/index-economics">
          <button className="page-button">Index</button>
        </Link>
        <Link to="/economics">
          <button className="page-button">Continue</button>
        </Link>
      </div>
      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
      <BadgePopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        level="Level 8"
        badgeImg="./achieve.png"
        heading="Sovereign Apprentice"
        subtitle="Beginner – Sovereign Skills Lab"
        mainImg="./sovereignapprentice.png"
        description="Awarded for taking the first steps toward financial self-sovereignty. You have begun learning hands-on skills such as secure key management, hardware wallets, and operating independently without trusted intermediaries.."
      />
    </div>
  );
}

export default DIY96;
